// Presentation for one transcript cue block.
//
// The panel used to be a single dark slab with each speaker's line painted in
// that speaker's colour. It was unreadable — the colours are picked to be
// findable on the compass against moving video, which is the opposite of what
// body text needs. So a cue is now a white card with black text, and the
// speaker's colour survives only as a 3px bar down the left edge: enough to
// read the panel as a conversation between named people, not enough to fight
// the words.
//
// THE GRADIENT AND THE ANIMATION ARE ONE MECHANISM. A block's opacity is a
// pure function of how far it sits from the cue being spoken, and the caller
// puts a CSS transition on that property. Advancing the active cue therefore
// re-animates every block at once, with no per-block animation state to track
// across a list that can run to hundreds of cues.

import React from 'react'

// Opacity by absolute distance from the spoken cue.
//
// Container opacity composites the whole block — white card and black text
// together — over a near-black panel, so a dimmed block is a grey card
// carrying grey text and the two converge as opacity falls. Measured against
// the panel ground, the steps below are:
//
//   spoken  1.00   white       21.0:1
//   ±1      0.62   158 grey     7.8:1   comfortably readable
//   ±2      0.45   115 grey     4.4:1   readable
//   tail    0.34    87 grey     2.9:1   texture, not reading material
//
// Lifted a shade on 11 Sep (from 0.6 / 0.38 / 0.24): Gavin — "the gray is a
// slight shade lighter. Only a slight shade lighter" — because on a poor
// screen the tail vanished. The steps are still clearly graded.
//
// THIS DELIBERATELY BREAKS 4.5:1 IN THE TAIL, and the reason is that the tail
// is not text anyone is reading. The line being spoken is at full strength and
// every line becomes that line as playback reaches it; the distance exists to
// show shape and position, the way a waveform does. An earlier pass held every
// step above 4.5:1 (1 / 0.82 / 0.68 / 0.58) and Kai's judgement on seeing it
// was that it read as barely graded at all — which is its own accessibility
// problem, because a gradient nobody can perceive is not telling anyone where
// they are. And to read ahead, hover: see CUE_HOVER below.
//
// If this ever needs to go back the other way, 0.52 is the lowest step that
// holds 4.5:1 against this panel. Do not raise the ramp above that without
// re-measuring: the panel ground is what sets the maths, and it changed once
// already.
const OPACITY_RAMP = [1, 0.62, 0.45, 0.34]

export const CUE_TRANSITION = 'opacity 300ms ease'

// HOVER TO READ AHEAD. Gavin, 11 Sep: "when I go over like this ... the text
// against that gray goes white", so you can read on past the spoken line while
// it plays. Hover only highlights — it never seeks; a click still does.
//
// The hovered card is drawn at full opacity on a fixed dark grey with white
// text (9.1:1), rather than white text on whatever grey its fade gives it: the
// ±1 cards are light grey, and white on those would read WORSE than the black
// text they already have. The spoken card is left alone — it is already the
// most legible thing on the panel.
//
// Pointer hover only (`hover: hover`), so a tap on a phone does not leave a
// card stuck grey. `!important` because the fade is an inline style.
export const CUE_CLASS = 'forum-transcript-cue'
export const CUE_HOVER_GROUND = 'rgb(72, 72, 72)'
export const cueHoverCss = `
@media (hover: hover) {
  .${CUE_CLASS}:not([data-spoken]):hover {
    opacity: 1 !important;
    background-color: ${CUE_HOVER_GROUND} !important;
    color: #fff !important;
  }
}
`

/**
 * Opacity for a cue block.
 *
 * `null` means no cue is being spoken yet — before playback starts, or between
 * cues. Everything renders at full strength then, because dimming the entire
 * panel to its tail value would make a paused player look broken. Sign is
 * ignored: a cue two ahead reads the same as a cue two behind.
 */
export function cueOpacity(distanceFromActive: number | null): number {
  if (distanceFromActive === null) return OPACITY_RAMP[0]
  if (!Number.isFinite(distanceFromActive)) return OPACITY_RAMP[OPACITY_RAMP.length - 1]
  const steps = Math.min(Math.abs(Math.trunc(distanceFromActive)), OPACITY_RAMP.length - 1)
  return OPACITY_RAMP[steps]
}

/**
 * The block itself, minus the speaker colour — only the caller knows which
 * participant spoke, so it sets `borderLeftColor` on top of this.
 *
 * `isOverlay` is portrait "podcast mode", where the panel covers a still
 * playing video. Solid white there would glare, so the card goes translucent
 * and blurs what is behind it; the shape and the animation are identical.
 */
export function cueBlock(isOverlay: boolean): React.CSSProperties {
  return {
    backgroundColor: isOverlay ? 'rgba(255,255,255,0.86)' : '#fff',
    ...(isOverlay
      ? { backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }
      : {}),
    color: '#000',
    borderRadius: '10px',
    padding: '10px 12px',
    // The speaker bar. Width and style live here so every block reserves the
    // same space; an unattributed cue simply leaves the colour transparent and
    // does not jump against its neighbours.
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    cursor: 'pointer',
    // 13px until Gavin, 3 Nov: "Transcript text a little larger".
    fontSize: '15px',
    lineHeight: 1.5,
  }
}

/**
 * How a speaker's face in the participant row is drawn: lit while they talk,
 * stepped back while someone else does, and plain when nobody has spoken yet
 * (`speakingId` null) — the same "don't dim a paused player" rule as cueOpacity.
 */
export function participantEmphasis(
  speakingId: string | null,
  participantId: string,
): { speaking: boolean; dimmed: boolean } {
  const speaking = speakingId !== null && speakingId === participantId
  return { speaking, dimmed: speakingId !== null && !speaking }
}
